import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { DEFAULT_CONTACT, type ContactInfo } from "@/lib/site-contact";
import { readSharedEdgeCache, writeSharedEdgeCache } from "@/server/site/edge-cache";

// Ojo: las fns admin (adminGetContactInfo/adminUpdateContactInfo) viven en
// contact-admin.fns.ts, NO acá — este archivo se importa desde el __root del
// sitio público, y el validator de zod de la fn de actualización arrastraba
// zod al JS inicial de todas las páginas públicas. Los helpers compartidos
// entre ambos archivos se exportan desde acá.

export const CONTACT_COLUMNS = "contacto_email, contacto_telefono, contacto_whatsapp" as const;

export interface ContactRow {
  contacto_email: string | null;
  contacto_telefono: string | null;
  contacto_whatsapp: string | null;
}

export function rowToContactInfo(row: ContactRow | null): ContactInfo {
  return {
    email: row?.contacto_email?.trim() || DEFAULT_CONTACT.email,
    phone: row?.contacto_telefono?.trim() || DEFAULT_CONTACT.phone,
    whatsapp: row?.contacto_whatsapp?.trim() || DEFAULT_CONTACT.whatsapp,
  };
}

// Cache en memoria a nivel de módulo (mismo patrón que getSiteImageSlots en
// site-image-slots.fns.ts): evitar una query en cada request de la home
// pesa más que la propagación instantánea de un cambio de contacto desde
// Preferencias — hasta 60s de demora es un trade-off aceptable. Además del
// nivel en memoria (L1, rápido pero por-isolate), hay un nivel de Cache API
// de Cloudflare (L2, edge-cache.ts) compartido entre isolates del mismo
// datacenter — un isolate reciclado (frecuente bajo baja carga) ya no paga
// el round-trip completo a Supabase, que es lo que explicaba el TTFB de
// campo (CrUX) mucho peor que el de laboratorio.
// Nota: tras el split de Workers (admin/público), adminUpdateContactInfo
// corre en el Worker admin y no puede invalidar el nivel L1 (memoria) del
// Worker público (isolates separados, sin memoria compartida) — por eso el
// borrado explícito de abajo solo apunta al nivel L2 (Cache API), que si
// está compartido a nivel de zona sí puede propagarse antes del TTL.
let contactCache: { data: ContactInfo; expiresAt: number } | null = null;
const CONTACT_CACHE_TTL = 60_000;
export const CONTACT_EDGE_CACHE_KEY = "public-contact-info";

/**
 * Lectura pública (sin autenticación) de los datos de contacto del sitio.
 * Se usa en el loader de la ruta raíz para SSR. Degrada a DEFAULT_CONTACT
 * ante cualquier error: el sitio público nunca debe romper por configuración.
 */
export const getPublicContactInfo = createServerFn({ method: "GET" }).handler(
  async (): Promise<ContactInfo> => {
    if (contactCache && Date.now() < contactCache.expiresAt) return contactCache.data;

    const fromEdge = await readSharedEdgeCache<ContactInfo>(CONTACT_EDGE_CACHE_KEY);
    if (fromEdge) {
      contactCache = { data: fromEdge, expiresAt: Date.now() + CONTACT_CACHE_TTL };
      return fromEdge;
    }

    try {
      const { data, error } = await supabaseAdmin
        .from("crm_configuracion")
        .select(CONTACT_COLUMNS)
        .eq("id", 1)
        .single();
      if (error) throw error;
      const resolved = rowToContactInfo(data as ContactRow);
      contactCache = { data: resolved, expiresAt: Date.now() + CONTACT_CACHE_TTL };
      await writeSharedEdgeCache(CONTACT_EDGE_CACHE_KEY, resolved, CONTACT_CACHE_TTL / 1000);
      return resolved;
    } catch (err) {
      console.error("[contact] getPublicContactInfo fallback to defaults:", err);
      return DEFAULT_CONTACT;
    }
  },
);
